import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useSite, UI } from "@/lib/site";
import { MaskLine, EASE_LUX } from "@/components/Reveal";
import { LogoMark, Ornament } from "@/components/Logo";
import { WaButton, InstaButton } from "@/components/WaButton";
import { useLenis, scrollToId } from "@/components/SmoothScroll";

const Rings = () => (
  <svg viewBox="0 0 400 400" className="pointer-events-none absolute inset-[-14%] h-[128%] w-[128%]" aria-hidden="true">
    <motion.circle cx="200" cy="200" r="190" fill="none" stroke="#b8893b" strokeOpacity="0.45" strokeWidth="0.6" strokeDasharray="2 7"
      animate={{ rotate: 360 }} transition={{ duration: 90, repeat: Infinity, ease: "linear" }} style={{ originX: "200px", originY: "200px" }} />
    <motion.circle cx="200" cy="200" r="168" fill="none" stroke="#b8893b" strokeOpacity="0.3" strokeWidth="0.5"
      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2.4, ease: EASE_LUX, delay: 0.6 }} />
  </svg>
);

export const Hero = () => {
  const { t, content } = useSite();
  const lenis = useLenis();
  const h = content?.hero || {};
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const markY = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [16, -16]), { stiffness: 70, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-20, 20]), { stiffness: 70, damping: 14 });
  const glowX = useTransform(mx, [-0.5, 0.5], ["30%", "70%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="grain relative min-h-[100svh] overflow-hidden bg-[#f3eadb]">
      <motion.img src="/media/silk-light.jpg" alt="" aria-hidden="true" style={{ y: bgY }}
        initial={{ scale: 1.25, opacity: 0 }} animate={{ scale: 1.1, opacity: 1 }} transition={{ duration: 2.2, ease: EASE_LUX }}
        className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#fbf7ef]/95 via-[#fbf7ef]/70 to-[#fbf7ef]/10" />
      <motion.div className="pointer-events-none absolute top-1/3 h-[50vw] w-[50vw] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-60 blur-3xl"
        style={{ left: glowX, background: "radial-gradient(circle, rgba(240,215,142,0.55), transparent 65%)" }} />

      <div className="relative mx-auto grid min-h-[100svh] max-w-[1400px] items-center gap-10 px-5 pb-24 pt-28 md:px-10 lg:grid-cols-12 lg:pt-24">
        <motion.div style={{ y: textY }} className="order-2 lg:order-1 lg:col-span-7">
          <motion.p className="eyebrow flex items-center gap-4" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.2 }}>
            <span className="h-px w-10 bg-[#8a6424]" />{t(h.eyebrow)}
          </motion.p>
          <h1 data-testid="hero-title" className="font-display mt-7 text-[3.1rem] font-medium leading-[0.95] tracking-[-0.02em] text-[#1d1712] sm:text-7xl lg:text-[6.4rem]">
            <MaskLine delay={0.35}>{t(h.line1)}</MaskLine>
            <MaskLine delay={0.5} className="gold-text italic">{t(h.line2)}</MaskLine>
            <MaskLine delay={0.65}>{t(h.line3)}</MaskLine>
          </h1>
          <motion.p className="mt-8 max-w-xl text-base leading-relaxed text-[#4a3f34] md:text-lg" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1 }}>
            {t(h.subtitle)}
          </motion.p>
          <motion.div className="mt-10 flex flex-wrap gap-3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.15 }}>
            <WaButton label={t(UI.discuss)} testId="hero-whatsapp-btn" />
            <InstaButton testId="hero-instagram-btn" />
            <button onClick={() => scrollToId(lenis, "directions")} className="btn-ghost" data-testid="hero-directions-btn">
              {t(UI.directions)} <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: markY }} className="order-1 hidden flex-col items-center justify-center md:flex lg:order-2 lg:col-span-5">
          <div className="relative w-44 sm:w-60 lg:w-[400px]" style={{ perspective: 900 }}>
            <Rings />
            <motion.div
              style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
              initial={{ opacity: 0, scale: 0.6, rotate: -25 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1.8, ease: EASE_LUX, delay: 0.2 }}
            >
              <motion.div animate={{ y: [0, -12, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}>
                <LogoMark className="w-full drop-shadow-[0_30px_40px_rgba(138,100,36,0.45)]" />
              </motion.div>
            </motion.div>
          </div>
          <motion.div className="mt-6 flex flex-col items-center lg:mt-10" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, delay: 1.1 }}>
            <span className="font-display text-4xl font-medium tracking-tight text-[#4a2f12] lg:text-6xl">Dari event</span>
            <Ornament className="mt-2 h-4 w-48 lg:w-72" />
          </motion.div>
        </motion.div>
      </div>

      <motion.div className="absolute bottom-7 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-[0.65rem] uppercase tracking-[0.3em] text-[#8a6424] md:flex" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }}>
        {t(UI.scroll)}
        <motion.span className="block h-10 w-px bg-[#8a6424]" animate={{ scaleY: [0, 1, 0], originY: 0 }} transition={{ duration: 2.2, repeat: Infinity }} />
      </motion.div>
    </section>
  );
};
